<script setup lang="ts">

import axios from 'axios'
import { defineEmits, ref, computed } from 'vue'
import { useRouter } from 'vue-router'

import * as z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'

import { AuthApi } from '@/services/api/auth.ts'

import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import LoginCard from '@/views/auth/components/LoginCard.vue'
import { ArrowLeftIcon, CheckIcon, Loader2Icon, XIcon } from 'lucide-vue-next'
import PasswordInput from '@/components/revio/input/PasswordInput.vue'

const router = useRouter()
const resetPasswordParams = {
  uid: router.currentRoute.value.query.uid as string,
  token: router.currentRoute.value.query.token as string,
}

const requestState = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
const emit = defineEmits<{
  (event: 'back'): void
}>()

const validations = {
  minLength: 12,
  maxLength: 128,
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasNumber: /[0-9]/,
  hasSpecialChar: /[^A-Za-z0-9]/,
}

const formSchema = toTypedSchema(
  z.object({
    password_1: z.string(),
    password_2: z.string(),
  })
)

const minLength = computed(() => form.values.password_1 && form.values.password_1.length >= validations.minLength)
const hasUppercase = computed(() => validations.hasUpperCase.test(form.values.password_1!))
const hasLowercase = computed(() => form.values.password_1 && validations.hasLowerCase.test(form.values.password_1!))
const hasNumber = computed(() => validations.hasNumber.test(form.values.password_1!))
const hasSpecial = computed(() => validations.hasSpecialChar.test(form.values.password_1!))
const isSamePassword = computed(() => form.values.password_1 && form.values.password_2 && form.values.password_1 === form.values.password_2)
const hasError = computed(() => !minLength.value || !hasUppercase.value || !hasLowercase.value || !hasNumber.value || !hasSpecial.value || !isSamePassword.value  )

const form = useForm({
  validationSchema: formSchema,
})

const onSubmit = form.handleSubmit(async (values) => {
  requestState.value = 'loading'
  try {
    await AuthApi.resetPassword({
      uid: resetPasswordParams.uid,
      token: resetPasswordParams.token,
      password_1: values.password_1,
      password_2: values.password_2,
    })
    requestState.value = 'success'
    emit('back')
  } catch (error) {
    if (axios.isAxiosError<400>(error)) {
      requestState.value = 'error'
      return
    }

    // TODO: sentry log error
    console.log('error', error)
  }
})
</script>

<template>
  <LoginCard>
    <form @submit="onSubmit">
      <CardHeader>
        <img
          src="/src/assets/media/revio/revio.svg"
          class="shrink-0 h-6 text-center p-0 m-0 mb-2.5"
          alt="Revio"
        />
        <CardTitle class="text-center">Set your Password</CardTitle>
        <CardDescription class="text-center">Enter your new password</CardDescription>
      </CardHeader>
<!-- TODO Abstract this in a component. -->
      <div
        v-if="requestState === 'error'"
        class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg"
      >
        The link you used is invalid or expired. Please request a new one.
      </div>

      <FormField v-slot="{ componentField }" name="password_1">
        <FormItem class="mb-4">
          <FormLabel>Password</FormLabel>
          <FormControl>
            <PasswordInput id="password_1" v-bind="componentField" placeholder="Enter password" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="password_2">
        <FormItem class="mb-4">
          <FormLabel>Confirm Password</FormLabel>
          <FormControl>
            <PasswordInput id="password_2" v-bind="componentField" placeholder="Confirm password" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <div class="mt-2 p-3 text-sm border rounded-lg">
        <p class="text-gray-600 mb-2">Password must contain:</p>
        <ul class="space-y-1">
          <li class="flex items-center gap-2" :class="minLength ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="minLength" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            At least 12 characters
          </li>
          <li class="flex items-center gap-2" :class="hasUppercase ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="hasUppercase" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            One uppercase letter
          </li>
          <li class="flex items-center gap-2" :class="hasLowercase ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="hasLowercase" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            One lowercase letter
          </li>
          <li class="flex items-center gap-2" :class="hasSpecial ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="hasSpecial" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            One special character
          </li>
          <li class="flex items-center gap-2" :class="hasNumber ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="hasNumber" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            One number
          </li>
          <li class="flex items-center gap-2" :class="isSamePassword ? 'text-green-600' : 'text-red-500'">
            <CheckIcon v-if="isSamePassword" class="h-4 w-4" />
            <XIcon v-else class="h-4 w-4" />
            Passwords match
          </li>
        </ul>
      </div>

      <Button
        v-if="requestState !== 'success'"
        class="mt-4 w-full items-center"
        type="submit"
        :disabled="hasError || requestState === 'loading'"
      >
        <Loader2Icon v-if="requestState === 'loading'"  class="size-4 animate-spin" />
        Set Password
      </Button>

      <Button
        v-if="requestState !== 'idle' && requestState !== 'loading'"
        class="mt-4 w-full"
        :variant="(requestState === 'success') ? 'default' : 'secondary'"
        @click.prevent="emit('back')"
      >
        <ArrowLeftIcon />
        Back to Login
      </Button>
    </form>
  </LoginCard>
</template>

<style scoped>
.back-button {
  @apply mt-4 w-full text-gray-600;
}
</style>
